/**
 * One frame, one clock.
 *
 * The canvas and the text overlay draw the same graph through two different
 * mediums, and both of them read state that changes *within* a frame — the
 * eased camera, and the eased node positions in `motion`. When they each ran
 * their own `requestAnimationFrame` loop, whichever happened to be registered
 * first won: the labels were positioned either one step ahead of the strands
 * they belong to or one step behind them.
 *
 * At 60 fps that skew is a few percent of one easing step and invisible. During
 * a re-framing — or on any machine dropping frames, where the step is clamped
 * to 120 ms — it is a visible slip between a caption and its node, which is
 * exactly the thing this overlay must never do.
 *
 * So there is one loop, in the canvas, and everything that has to agree with
 * what was just painted subscribes here and runs after it.
 *
 * THE LOOP RESTS (see `cadence.ts`), so a frame is no longer something a
 * subscriber can wait for and be sure of getting. Two consequences live here.
 * A fresh subscription is owed one: a card or a mirror that mounts over a still
 * graph places or reveals itself on its first frame, and on a resting loop under
 * reduced motion that frame would otherwise never come. And anything that moves
 * a mark without writing the store asks for one by name through `requestFrame`.
 */
type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Whether the time since the previous frame was the loop's own choice — this
 * frame came at the resting rate, or is the first after a hold — rather than the
 * machine's. Set by the loop before each draw. A subscriber that times frames to
 * judge the machine must skip these: a rested gap is fifty milliseconds by
 * design, and read as a slow frame it is indistinguishable from jank.
 */
export const frameGap = { deliberate: false }

/**
 * Whether this frame is one the loop OWED (`cadence.ts`): its first, the first
 * after a resize, the first after a palette change — drawn even through a sheet's
 * hold, because the picture before it was cleared or is the other theme's. Set
 * by the loop before each draw. A subscriber that keeps a copy of the canvas
 * copies this frame whatever its own throttle says: under a sheet no other frame
 * comes to catch the copy up, and the glass's mirror went on showing the night
 * field through the sheet somebody had just picked Light in.
 */
export const owedFrame = { now: false }

/** Whatever the mounted canvas answers a request with. One canvas at a time. */
let waker: (() => void) | null = null
/** …and a repaint, the frame it owes even through a hold (`cadence.ts`). */
let repainter: (() => void) | null = null

/** The canvas registers how it is woken and how it is made to repaint; returns
 *  the unregister. */
export function setFrameWaker(fn: () => void, repaint?: () => void): () => void {
  waker = fn
  repainter = repaint ?? null
  return () => {
    if (waker === fn) {
      waker = null
      repainter = null
    }
  }
}

/** Ask the canvas for a frame soon. Idempotent, and a no-op with no canvas up. */
export function requestFrame() {
  waker?.()
}

/**
 * Ask for a frame that is drawn even while a sheet holds the loop: the canvas
 * was cleared (resized), or the palette changed under a sheet whose glass shows
 * the field through it. `requestFrame` is right for everything else — a frame
 * that nobody can see is exactly what the hold is there to skip.
 */
export function requestRepaint() {
  const ask = repainter ?? waker
  ask?.()
}

/** Subscribe; returns an unsubscribe. Called immediately after each canvas draw. */
export function onAfterDraw(fn: Listener): () => void {
  listeners.add(fn)
  requestFrame()
  return () => {
    listeners.delete(fn)
  }
}

export function emitAfterDraw() {
  // A throwing subscriber must not take the render loop down with it.
  for (const fn of listeners) {
    try {
      fn()
    } catch (err) {
      console.error(err)
    }
  }
}
